<script setup lang="ts">
import { computed } from "vue";
import { UiCard, UiChip, UiText } from "@shopexperts/shared";
import type { BannedEmail } from "@shopexperts/legacy-api";
import BanListActionButton from "./BanListActionButton.vue";
import BanListDetail from "./BanListDetail.vue";
import {
    contextEntries,
    formatBanDate,
    formatSource,
    sourceChipVariant,
} from "@/app/composables/ban-list/banListFormatters";

const props = defineProps<{
    ban: BannedEmail;
    isBusy?: boolean;
}>();

const emit = defineEmits<{
    (e: "unban"): void;
    (e: "filter-by-rule", ruleId: number): void;
}>();

const context = computed(() => contextEntries(props.ban.context));
</script>

<template>
    <UiCard padding="md" class="transition-shadow hover:shadow-md">
        <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div class="flex min-w-0 flex-1 flex-col gap-2">
                    <div class="flex flex-wrap items-center gap-2">
                        <UiText as="p" variant="h6" class="font-semibold break-all text-se-dark">
                            {{ ban.email }}
                        </UiText>
                        <UiChip :text="formatSource(ban.source)" :variant="sourceChipVariant(ban.source)" size="sm" />
                    </div>

                    <button
                        v-if="ban.ban_rule"
                        type="button"
                        class="w-fit text-left text-sm text-se-dark underline decoration-se-gray-300 underline-offset-2 hover:decoration-se-dark"
                        @click="emit('filter-by-rule', ban.ban_rule.id)"
                    >
                        Rule: {{ ban.ban_rule.name }}
                    </button>
                    <UiText
                        v-else
                        as="p"
                        variant="p-small"
                        class="text-se-gray-600"
                        text="No rule attached."
                    />

                    <div v-if="ban.matched_terms.length" class="flex flex-wrap items-center gap-2">
                        <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Matched terms" />
                        <UiChip
                            v-for="term in ban.matched_terms"
                            :key="term"
                            :text="term"
                            size="sm"
                            rounded="full"
                            variant="neutral"
                        />
                    </div>
                </div>

                <div class="flex flex-wrap items-center gap-3 xl:shrink-0">
                    <BanListActionButton
                        label="Unban"
                        icon="lock-open"
                        tone="danger"
                        :disabled="isBusy"
                        @click="emit('unban')"
                    />
                </div>
            </div>

            <div v-if="ban.matched_text" class="rounded-md bg-se-gray-50 p-3">
                <UiText as="p" variant="caption-normal" class="text-xs uppercase tracking-wider text-se-gray-500" text="Offending text" />
                <p class="mt-1 font-mono text-xs break-words text-se-dark">{{ ban.matched_text }}</p>
            </div>

            <div class="grid grid-cols-1 gap-3 border-t border-se-gray-100 pt-4 sm:grid-cols-2 lg:grid-cols-4">
                <BanListDetail label="Banned at" :value="formatBanDate(ban.banned_at, 'Unknown')" />
                <BanListDetail label="IP at submission" :value="ban.ip_address" />
                <!--
                    `user_id` is only written when the ban itself established who
                    was submitting; a manual admin ban still locks the account but
                    records no id, so this is not a "was the account locked?" flag.
                -->
                <BanListDetail label="Linked user" :value="ban.user_id ? `User #${ban.user_id}` : 'None recorded'" />
                <BanListDetail label="Notes" :value="ban.notes" />
            </div>

            <div v-if="context.length" class="flex flex-wrap items-center gap-2 border-t border-se-gray-100 pt-4">
                <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Submission context" />
                <UiChip
                    v-for="entry in context"
                    :key="entry.label"
                    :text="`${entry.label}: ${entry.value}`"
                    size="sm"
                    variant="outline"
                />
            </div>
        </div>
    </UiCard>
</template>
